import React, { useEffect, useState } from "react";

import {
  useOutletContext,
  useSearchParams,
} from "react-router-dom";
import toast from "react-hot-toast";
import { getApiErrorMessage } from "../../utils/apiErrorMessage";
import {
  type PaginatedResponse,
  type ProviderManproProjectDto,
} from "../../api/manpro";
import type { LayoutOutletContext } from "../../types/auth";
import {
  ProjectManagementProvider,
  type ProjectManagementContextValue,
} from "../../features/project-management/context";
import { useFacilityManagementContext } from "../../features/facility/context/FacilityManagementContext";
import ProjectManagementModals from "../../features/project-management/components/ProjectManagementModals";
import ProjectManagementTabs from "../../features/project-management/components/ProjectManagementTabs";
import ProjectManagementFinance from "../../features/project-management/components/ProjectManagementFinance";
import PlanningSection from "../../features/project-management/components/PlanningSection";
import ExecutionSection from "../../features/project-management/components/ExecutionSection";
import ValidationSection from "../../features/project-management/components/ValidationSection";
import ReportingSection from "../../features/project-management/components/ReportingSection";
import { TeamManagementPage } from "../../components/teamManagement/TeamManagementPage";
import ProjectDetailsSkeleton from "../../features/project-management/components/ProjectDetailsSkeleton";
import FinalizeBaselineModal from "../../features/project-management/components/FinalizeBaselineModal";
import NewProjectModal from "../../features/project-management/components/NewProjectModal";
import ProjectWorkspaceOverview from "../../features/project-management/components/ProjectWorkspaceOverview";
import ProviderProjectWorkspace from "../../features/project-management/components/ProviderProjectWorkspace";
import instance from "../../api/axios";

import { useProjectQueries } from "./hooks/useProjectQueries";
import { useProjectMutations } from "./hooks/useProjectMutations";
import { useProjectState } from "./hooks/useProjectState";
import { useProjectKpi } from "./hooks/useProjectKpi";
import { isInternalManproProject } from "../../features/project-management/isInternalManproProject";
import type {
  Identifier,
  Technician,
  ProviderCard,
  EvidenceItem,
  VendorItem,
  InventoryItem,
  ProjectListItem,
  ValidationItem,
  BaselineSaveMode,
} from "./types";


const getRowsFromResponse = <T,>(response?: PaginatedResponse<T> | T[]) =>
  Array.isArray(response) ? response : response?.data || [];


const ProjectManagement = () => {

  const { userRole, permissions = [] } =
    useOutletContext<LayoutOutletContext>();
  const { technicians: sharedTechnicians } = useFacilityManagementContext();

  const [searchParams, setSearchParams] = useSearchParams();
  const categoryId = searchParams.get("category_id");
  const projectIdFromUrl = searchParams.get("project_id") || searchParams.get("projectId");

  const tabFromUrl = (searchParams.get("tab") as ProjectManagementContextValue["activeTab"]) || "planning";
  const [activeTab, setActiveTabLocal] = useState<ProjectManagementContextValue["activeTab"]>(tabFromUrl);

  useEffect(() => {
    const tab = searchParams.get("tab") as ProjectManagementContextValue["activeTab"];
    if (tab && tab !== activeTab) {
      setActiveTabLocal(tab);
    }
  }, [searchParams, activeTab]);

  const setActiveTab = (tab: ProjectManagementContextValue["activeTab"] | ((prev: ProjectManagementContextValue["activeTab"]) => ProjectManagementContextValue["activeTab"])) => {
    if (typeof tab === 'function') {
        const nextTab = tab(activeTab);
        setActiveTabLocal(nextTab);
        setSearchParams((prev) => {
          prev.set("tab", nextTab);
          return prev;
        });
    } else {
        setActiveTabLocal(tab);
        setSearchParams((prev) => {
          prev.set("tab", tab);
          return prev;
        });
    }
  };
  const [selectedProvider, setSelectedProvider] = useState<ProviderCard | null>(
    null,
  );
  const [isWorkspaceOpen, setIsWorkspaceOpen] = useState(Boolean(projectIdFromUrl));
  const [workspaceSource, setWorkspaceSource] = useState<
    "draft" | "project" | "new" | null
  >(projectIdFromUrl ? "project" : null);
  const [isNewProjectModalOpen, setIsNewProjectModalOpen] = useState(false);
  const [newProjectName, setNewProjectName] = useState("");

  const [activeProjectId, setActiveProjectId] = useState<Identifier | null>(
    projectIdFromUrl ? (isNaN(Number(projectIdFromUrl)) ? projectIdFromUrl : Number(projectIdFromUrl)) : null,
  );

  const [isVendorModalOpen, setIsVendorModalOpen] = useState(false);

  const {
    activeProjectQuery,
    providersQuery,
    vendorsQuery,
    projectsQuery,
    providerManproProjectsQuery,
  } = useProjectQueries({
    userRole,
    categoryId,
    activeProjectId,
    isVendorModalOpen,
  });

  const {
    baselineData,
    setBaselineData,
    executionData,

    taskTeams,
    setTaskTeams,
    managerNote,
    setManagerNote,
    getDefaultBaseline,
  } = useProjectState(activeProjectQuery);

  const {
    invalidateProjectData,
    updateTaskProgressMutation,
    toggleResourceMutation,
    addExpenseMutation,
    assignTechniciansMutation,
    validateTaskMutation,
    saveBaselineMutation,
    createProjectMutation,
    deleteProjectMutation,
    updateManagerNoteMutation,
  } = useProjectMutations({
    activeProjectId,
    categoryId,
  });

  useProjectKpi(baselineData, executionData, managerNote);

  const allTechnicians = sharedTechnicians as Technician[];
  const providers = providersQuery.data || [];
  const isLoadingProviders = providersQuery.isLoading || providersQuery.isFetching;
  const architectVendors = vendorsQuery.data || [];
  const isLoadingVendors = vendorsQuery.isLoading || vendorsQuery.isFetching;
  const allProjects = projectsQuery.data || [];
  const providerManproProjects = getRowsFromResponse(providerManproProjectsQuery.data);
  const [selectedProviderProjectId, setSelectedProviderProjectId] =
    useState<Identifier | null>(null);

  const selectedProviderProject =
    providerManproProjects.find((project) => String(project.id) === String(selectedProviderProjectId)) ||
    providerManproProjects[0] ||
    null;

  const totalActualExpense = executionData.actualExpenses.reduce(
    (sum, e) => sum + e.amount,
    0,
  );





  const handleAddExpenseApi = async (
    title: string,
    amount: number,
    description: string,
    receipt: File | null,
    date?: string,
    options?: { kind?: "contingency" | "operational"; category?: string },
  ) => {
    if (!activeProjectId) {
      toast.error("Silakan pilih proyek terlebih dahulu.");
      return false;
    }
    const kind = options?.kind ?? "contingency";
    if (kind === "contingency" && !receipt) {
      toast.error("Struk wajib diunggah untuk biaya tak terduga.");
      return false;
    }
    if (kind === "operational" && !options?.category) {
      toast.error("Pilih kategori RAB terlebih dahulu.");
      return false;
    }
    try {
      const formData = new FormData();
      formData.append("kind", kind);
      formData.append("title", title);
      formData.append("amount", amount.toString());
      formData.append("description", description);
      if (kind === "operational") {
        formData.append("category", options!.category as string);
      } else {
        formData.append("category", "Kontingensi");
      }
      if (receipt) {
        formData.append("receipt", receipt);
      }
      if (date) {
        formData.append("date", date);
      }

      await toast.promise(
        addExpenseMutation.mutateAsync({
          projectId: activeProjectId,
          data: formData as any,
        }),
        {
          loading: kind === "operational" ? "Mencatat pengeluaran RAB..." : "Mencatat pemakaian dana kontingensi...",
          success: kind === "operational" ? "Pengeluaran RAB dicatat." : "Pemakaian dana kontingensi berhasil dicatat.",
          error: (err) => getApiErrorMessage(
            err,
            kind === "operational" ? "Gagal mencatat pengeluaran RAB." : "Gagal mencatat biaya kontingensi.",
          ),
        }
      );
      await invalidateProjectData();
      return true;
    } catch (err) {
      console.error(err);
      return false;
    }
  };

  const handleTaskProgress = async (
    taskId: Identifier,
    progress: number,
    notes = "",
  ) => {
    if (!activeProjectId) {
      toast.error("Silakan buat atau pilih proyek terlebih dahulu.");
      return;
    }
    try {
      await toast.promise(
        updateTaskProgressMutation.mutateAsync({
          projectId: activeProjectId,
          taskId,
          progress,
          notes,
        }),
        {
          loading: "Menyimpan progres tugas...",
          success: "Progres tugas berhasil diperbarui.",
          error: "Gagal mengupdate progres tugas.",
        },
      );

      await invalidateProjectData();
    } catch (err) {
      console.error("Failed to update task progress:", err);
    }
  };

  const handleToggleMaterial = async (
    type: "vendor" | "inventory" | string,
    id: Identifier,
    _cost?: number,
  ) => {
    if (!activeProjectId) {
      toast.error("Silakan buat atau pilih proyek terlebih dahulu.");
      return;
    }
    if (type === "vendor" || type === "inventory") {
      try {
        await toast.promise(
          toggleResourceMutation.mutateAsync({
            projectId: activeProjectId,
            resourceId: id,
          }),
          {
            loading: "Menyimpan perubahan resource...",
            success: "Status resource berhasil diperbarui.",
            error: "Gagal memperbarui status resource.",
          },
        );

        await invalidateProjectData();
      } catch (err) {
        console.error("Failed to toggle resource:", err);
      }
    }
  };

  const totalCalculatedExpense =
    totalActualExpense +
    (baselineData.vendors || [])
      .filter((v) =>
        (executionData.usedVendorIds || []).includes(v.id ?? "__missing__"),
      )
      .reduce((sum, v) => sum + (v.cost || 0), 0);

  const [isInventoryModalOpen, setIsInventoryModalOpen] = useState(false);
  const [newVendor, setNewVendor] = useState<VendorItem>({
    id: 0,
    name: "",
    task: "",
    cost: 0,
    vendor_id: null,
  });
  const [newItem, setNewItem] = useState<Pick<InventoryItem, "name">>({
    name: "",
  });

  // Team Management State — persisted
  const [isTeamModalOpen, setIsTeamModalOpen] = useState(false);
  const [activeTaskId, setActiveTaskId] = useState<Identifier | null>(null);
  const [techSearch, setTechSearch] = useState("");
  const [isEvidenceModalOpen, setIsEvidenceModalOpen] = useState(false);
  const [activeEvidence, setActiveEvidence] = useState<EvidenceItem[]>([]);

  // History Modal State
  const [isHistoryModalOpen, setIsHistoryModalOpen] = useState(false);

  // Validation State (Computed from Execution)
  const validationItems: ValidationItem[] = (baselineData.tasks || [])
    .filter(
      (t) =>
        t.status === "awaiting_validation" ||
        t.status === "waiting_for_approval" ||
        ((executionData.taskProgress || {})[t.id] === 100 && !(executionData.validatedTasks || []).includes(t.id))
    )
    .map((t) => {
      const assignedTechs = taskTeams[t.id] || [];
      const techNames =
        assignedTechs.length > 0
          ? assignedTechs
            .map((tech) => `${tech.first_name} ${tech.last_name}`)
            .join(", ")
          : "Tim Lapangan (Belum Di-assign)";
      const techInitials =
        assignedTechs.length > 0 && assignedTechs[0].first_name
          ? `${assignedTechs[0].first_name[0]}${assignedTechs[0].last_name?.[0] || ""}`
          : "TT";

      return {
        id: t.id,
        name: t.name,
        location: "Area Proyek",
        evidence: t.evidence || [],
        notes: t.notes || "",
        technician: techNames,
        techInitials: techInitials,
        duration: "Menunggu",
        status: "Menunggu Validasi",
        createdAt: t.created_at || new Date().toISOString(),
        completedAt: t.updated_at || new Date().toISOString(),
        beforeImg:
          "https://images.unsplash.com/photo-1584622650111-993a426fbf0a?auto=format&fit=crop&q=80&w=600",
        afterImg:
          "https://images.unsplash.com/photo-1527515637462-cff94eecc1ac?auto=format&fit=crop&q=80&w=600",
        checklists:
          (baselineData.global_checklists || []).length > 0
            ? baselineData.global_checklists.map((checklist, index) => ({
              id: `${t.id}_${index + 1}`,
              label: typeof checklist === 'string' ? checklist : (checklist.label || checklist.name || ""),
            }))
            : [
              {
                id: `${t.id}_1`,
                label: "Area bebas dari debu dan kotoran sisa pekerjaan?",
              },
              {
                id: `${t.id}_2`,
                label:
                  "Peralatan telah dikembalikan ke tempat penyimpanan semula?",
              },
              {
                id: `${t.id}_3`,
                label:
                  "Kualitas pekerjaan telah sesuai dengan standar minimal (SLA)?",
              },
            ],
      };
    });

  const [activeValidationIdState, setActiveValidationId] =
    useState<Identifier | null>(null);

  const activeValidationId =
    activeValidationIdState &&
      validationItems.find((v) => v.id === activeValidationIdState)
      ? activeValidationIdState
      : validationItems.length > 0
        ? validationItems[0].id
        : null;

  const [validationChecklist, setValidationChecklist] = useState<
    Record<string, boolean>
  >({});

  const [isRejectModalOpen, setIsRejectModalOpen] = useState(false);
  const [rejectReason, setRejectReason] = useState("");
  const [isFinalizeModalOpen, setIsFinalizeModalOpen] = useState(false);

  const activeValidationItem = validationItems.find(
    (item) => item.id === activeValidationId,
  );
  const isAllChecked =
    activeValidationItem &&
    activeValidationItem.checklists.every((c) => validationChecklist[c.id]);

  const handleValidationCheck = (id: Identifier) => {
    setValidationChecklist((prev) => ({ ...prev, [id]: !prev[id] }));
  };



  useEffect(() => {
    if (!projectsQuery.isSuccess) return;

    if (allProjects.length > 0) {
      if (isWorkspaceOpen && workspaceSource === "project") {
        setActiveProjectId((prev) => prev ?? allProjects[0].id);
      }
      return;
    }

    setBaselineData(getDefaultBaseline());

    setTaskTeams({});
    setManagerNote("");
    setActiveProjectId(null);
  }, [allProjects, projectsQuery.isSuccess]);

  useEffect(() => {
    if (projectIdFromUrl) {
      const parsedId = isNaN(Number(projectIdFromUrl)) ? projectIdFromUrl : Number(projectIdFromUrl);
      setActiveProjectId(parsedId);
      setIsWorkspaceOpen(true);
      setWorkspaceSource("project");
      if (userRole === "provider") {
        setSelectedProviderProjectId(parsedId);
      }
    }
  }, [projectIdFromUrl, userRole]);

  useEffect(() => {
    if (userRole !== "provider" || selectedProviderProjectId || providerManproProjects.length === 0) {
      return;
    }

    const projectFromUrl = projectIdFromUrl
      ? providerManproProjects.find((project) => String(project.id) === projectIdFromUrl)
      : null;

    setSelectedProviderProjectId((projectFromUrl || providerManproProjects[0]).id);
  }, [providerManproProjects, projectIdFromUrl, selectedProviderProjectId, userRole]);


  useEffect(() => {
    if (providersQuery.error) toast.error("Gagal memuat daftar provider.");
  }, [providersQuery.error]);

  useEffect(() => {
    if (vendorsQuery.error && isVendorModalOpen) {
      toast.error("Gagal memuat data vendor.");
    }
  }, [isVendorModalOpen, vendorsQuery.error]);


  useEffect(() => {
    // Scroll ke atas setiap kali ganti tab
    window.scrollTo({ top: 0, behavior: "smooth" });
  }, [projectsQuery.error]);

  useEffect(() => {
    if (projectsQuery.error) toast.error("Gagal memuat daftar proyek.");
  }, [projectsQuery.error]);

  useEffect(() => {
    if (providerManproProjectsQuery.error) {
      toast.error("Gagal memuat kelola proyek provider.");
    }
  }, [providerManproProjectsQuery.error]);

  useEffect(() => {
    if (activeProjectQuery.error) toast.error("Gagal memuat detail proyek.");
  }, [activeProjectQuery.error]);


  const handleAssignTech = async (taskId: Identifier, tech: Technician) => {
    if (!activeProjectId) {
      toast.error("Silakan buat atau pilih proyek terlebih dahulu.");
      return;
    }
    const currentTeam = taskTeams[taskId] || [];
    const isAlreadyAssigned = currentTeam.some((t) => t.id === tech.id);
    const newTeamIds = isAlreadyAssigned
      ? currentTeam.filter((t) => t.id !== tech.id).map((t) => t.id)
      : [...currentTeam, tech].map((t) => t.id);

    try {
      await toast.promise(
        assignTechniciansMutation.mutateAsync({
          projectId: activeProjectId,
          taskId,
          servicemanIds: newTeamIds,
        }),
        {
          loading: "Menyimpan tim teknisi...",
          success: "Tim teknisi berhasil diperbarui.",
          error: "Gagal meng-assign teknisi.",
        },
      );
      setTaskTeams((prev) => ({
        ...prev,
        [taskId]: isAlreadyAssigned
          ? currentTeam.filter((t) => t.id !== tech.id)
          : [...currentTeam, tech],
      }));
      await invalidateProjectData();
    } catch (err) {
      console.error("Failed to assign technician:", err);
    }
  };

  const handleApproveValidation = async (
    activeValidationId: Identifier,
    activeValidationItem: ValidationItem,
  ) => {
    if (!activeProjectId) {
      toast.error("Silakan buat atau pilih proyek terlebih dahulu.");
      return;
    }
    try {
      await toast.promise(
        validateTaskMutation.mutateAsync({
          projectId: activeProjectId,
          taskId: activeValidationId,
          action: "approve",
        }),
        {
          loading: "Mengirim approval...",
          success: `Pekerjaan "${activeValidationItem.name}" berhasil di-approve.`,
          error: "Gagal mengirim approval.",
        },
      );


      await invalidateProjectData();
    } catch (err) {
      console.error("Failed to approve validation:", err);
    }
  };

  const handleRejectValidation = async (
    activeValidationId: Identifier,
    rejectReason: string,
  ) => {
    if (!activeProjectId) {
      toast.error("Silakan buat atau pilih proyek terlebih dahulu.");
      return;
    }
    try {
      await toast.promise(
        validateTaskMutation.mutateAsync({
          projectId: activeProjectId,
          taskId: activeValidationId,
          action: "reject",
          reason: rejectReason,
        }),
        {
          loading: "Mengirim penolakan...",
          success: "Pekerjaan dikembalikan ke teknisi.",
          error: "Gagal mengirim penolakan.",
        },
      );
      handleTaskProgress(activeValidationId, 50);
      await invalidateProjectData();
    } catch (err) {
      console.error("Failed to reject validation:", err);
    }
  };

  const handleSaveBaseline = async (saveMode: BaselineSaveMode = "final", isConfirmed: boolean = false) => {
    if (!activeProjectId) {
      toast.error("Silakan buat atau pilih proyek terlebih dahulu.");
      return;
    }

    if (!isInternalManproProject(activeProjectQuery.data ?? selectedProviderProject) && baselineData.payment_type === 'termijn' && (baselineData.termijns || []).length > 0) {
      const hasEmptyTitle = (baselineData.termijns || []).some(row => !row.title || row.title.trim() === '');
      if (hasEmptyTitle) {
        toast.error("Nama Termin tidak boleh kosong! Silakan isi semua nama termin.");
        return;
      }

      const totalPercentage = (baselineData.termijns || []).reduce(
        (acc, row) => acc + (Number(row.percentage) || 0),
        0,
      );
      const totalAmount = (baselineData.termijns || []).reduce(
        (acc, row) => acc + (Number(row.amount) || 0),
        0,
      );
      const expectedBudget = Number(baselineData.budget || 0);

      const roundedPct = Math.round(totalPercentage * 100) / 100;
      if (roundedPct !== 100) {
        toast.error(`Total persentase termin harus tepat 100% (saat ini ${roundedPct}%). Silakan sesuaikan sebelum menyimpan.`);
        return;
      }

      if (expectedBudget > 0 && Math.abs(totalAmount - expectedBudget) > 100) {
        toast.error(`Total nominal uang termin (Rp ${totalAmount.toLocaleString('id-ID')}) harus sama persis dengan total RAB (Rp ${expectedBudget.toLocaleString('id-ID')}).`);
        return;
      }
    }

    if (saveMode === "final" && !isConfirmed) {
      setIsFinalizeModalOpen(true);
      return;
    }

    try {
      // Build milestone payload including WBS tasks
      const milestonesWithTasks = baselineData.milestones.map((m) => ({
        label: m.label,
        date: m.date,
        weight: m.weight,
        tasks: baselineData.tasks
          .filter((t) => t.milestone_id === m.id)
          .map((t) => ({ name: t.name, weight: t.weight })),
      }));

      // Build resources payload (vendors + inventory)
      const resources = [
        ...(baselineData.vendors || []).map((v) => ({
          type: "vendor",
          name: v.name,
          description: v.task || "",
          estimated_cost: v.cost || 0,
          vendor_id: v.vendor_id || null,
          status: v.status || "Pending",
        })),
        ...(baselineData.inventory || []).map((i) => ({
          type: "inventory",
          name: i.name,
          description: "",
          estimated_cost: 0,
          vendor_id: null,
          status: i.status || "Pending",
        })),
      ];

      const data = {
        name: baselineData.projectName,
        kickoff_date: baselineData.kickoff,
        deadline_date: baselineData.deadline,
        budget: baselineData.budget,
        budget_allocations: baselineData.allocation || [],
        team_members: baselineData.teamMembers || [],
        milestones: milestonesWithTasks,
        resources: resources,
        payment_type: baselineData.payment_type || 'full',
        termijns: baselineData.termijns || [],
      };

      await toast.promise(
        saveBaselineMutation.mutateAsync({
          projectId: activeProjectId,
          data,
          saveMode,
        }),
        {
          loading: saveMode === "draft" ? "Menyimpan draft perencanaan..." : "Finalisasi baseline proyek...",
          success: saveMode === "draft" ? "Draft rencana berhasil disimpan." : "Rencana kerja final berhasil difinalisasi.",
          error: (err: any) => {
            const apiMessage = err?.response?.data?.errors?.[0]?.message;
            return apiMessage || "Gagal menyimpan baseline.";
          },
        },
      );
      // Invalidate semua data proyek agar status terbaru (misal: pending_owner_approval atau draft)
      // langsung terefleksi di card list sidebar maupun workspace header.
      await invalidateProjectData();
    } catch (err) {
      console.error("Failed to save baseline:", err);
    }
  };

  const handleSaveBaselineDraft = () => handleSaveBaseline("draft");

  const handleFinalizeBaseline = () => handleSaveBaseline("final");

  const handleNewProject = () => {
    setNewProjectName("Proyek Baru");
    setIsNewProjectModalOpen(true);
  };

  const handleCreateNewProject = async (e?: React.FormEvent) => {
    if (e) e.preventDefault();
    const name = newProjectName.trim();
    if (!name) return;

    try {
      setIsNewProjectModalOpen(false);
      const proj = await toast.promise(
        createProjectMutation.mutateAsync({ name }),
        {
          loading: "Membuat proyek baru...",
          success: "Proyek berhasil dibuat.",
          error: "Gagal membuat proyek baru.",
        },
      );
      await invalidateProjectData(proj.id);
      setActiveProjectId(proj.id);
      setWorkspaceSource("project");
      setActiveTab("planning");
      setIsWorkspaceOpen(true);
      setSearchParams((prev) => {
        const next = new URLSearchParams(prev);
        next.set("project_id", String(proj.id));
        return next;
      });
    } catch (err) {
      console.error("Failed to create project:", err);
    }
  };

  const handleLoadProject = (proj: ProjectListItem) => {
    setActiveProjectId(proj.id);
    setIsHistoryModalOpen(false);
    setActiveTab("planning");
    setSearchParams((prev) => {
      const next = new URLSearchParams(prev);
      next.set("project_id", String(proj.id));
      return next;
    });
  };

  const openProviderProjectWorkspace = (project: ProviderManproProjectDto) => {
    // Provider hanya boleh membuka workspace dari project yang sudah dibuat backend saat Owner accept bid.
    setSelectedProviderProjectId(project.id);
    setActiveProjectId(project.id);
    setWorkspaceSource("project");
    setActiveTab("planning");
    setIsWorkspaceOpen(true);
    setSearchParams((prev) => {
      const next = new URLSearchParams(prev);
      next.set("project_id", String(project.id));
      return next;
    });
  };

  const handleDeleteProject = async (proj: ProjectListItem) => {
    if (
      !window.confirm(
        `Hapus proyek "${proj.name}"? Data tidak bisa dikembalikan.`,
      )
    ) {
      return;
    }

    try {
      await toast.promise(deleteProjectMutation.mutateAsync(proj.id), {
        loading: "Menghapus proyek...",
        success: "Proyek berhasil dihapus.",
        error: "Gagal menghapus proyek dari server.",
      });

      if (activeProjectId === proj.id) {
        setActiveProjectId(null);
      }

      await invalidateProjectData();
    } catch (err) {
      console.error("Failed to delete project:", err);
    }
  };

  const handleSaveManagerNote = async () => {
    if (!activeProjectId) {
      toast.error("Silakan buat atau pilih proyek terlebih dahulu.");
      return;
    }

    try {
      await toast.promise(
        updateManagerNoteMutation.mutateAsync({
          projectId: activeProjectId,
          note: managerNote,
        }),
        {
          loading: "Menyimpan catatan manajer...",
          success: "Catatan manajer berhasil disimpan.",
          error: "Gagal menyimpan catatan manajer.",
        },
      );

      await invalidateProjectData();
    } catch (err) {
      console.error("Failed to save manager note:", err);
    }
  };


  const materialBudget = (() => {
    const materialItem = baselineData.allocation.find(item => item.id === 'material');
    return (baselineData.budget * (materialItem?.percentage || 0)) / 100;
  })();
  const currentVendorCosts = baselineData.vendors.reduce(
    (sum, v) => sum + (v.cost || 0),
    0,
  );

  const handleBaselineChange = (
    field: string,
    value: any,
    index: number | null = null,
    subfield: string | null = null,
  ) => {
    setBaselineData((prev) => {
      const newData = { ...prev };
      if (index !== null && subfield) {
        // Handle array of objects (milestones/tasks/vendors/inventory)
        if (["milestones", "tasks", "vendors", "inventory"].includes(field)) {
          newData[field][index] = {
            ...newData[field][index],
            [subfield]: value,
          };
        }
      } else if (subfield) {
        // Handle nested object (allocation)
        newData[field] = { ...newData[field], [subfield]: value };
      } else {
        // Handle direct field
        newData[field] = value;
      }
      return newData;
    });
  };

  const isCleaning =
    selectedProvider?.modules?.includes("cleaning") ||
    permissions.some((p) => p.includes("cleaning"));
  const isConstruction = !isCleaning;

  const openProjectWorkspace = (project: ProjectListItem) => {
    setActiveProjectId(project.id);
    setWorkspaceSource("project");
    setIsWorkspaceOpen(true);
    setActiveTab("planning");
    setSearchParams((prev) => {
      const next = new URLSearchParams(prev);
      next.set("project_id", String(project.id));
      return next;
    });
  };

  const handleShareLink = async () => {
    if (!activeProjectId) return;
    try {
      const toastId = toast.loading('Membuat link...');
      const res = await instance.post(`/v1/projects/${activeProjectId}/public-link`);
      if (res.data.status === 'success') {
        const link = `${window.location.origin}/guest/manpro/${res.data.public_token}`;
        await navigator.clipboard.writeText(link);
        toast.success('Link berhasil disalin ke clipboard!', { id: toastId });
      }
    } catch (error) {
      toast.error('Gagal membuat link proyek.');
    }
  };

  const closeWorkspace = () => {
    setIsWorkspaceOpen(false);
    setWorkspaceSource(null);
    setSearchParams((prev) => {
      const next = new URLSearchParams(prev);
      next.delete("project_id");
      next.delete("projectId");
      return next;
    });
  };

  const isPendingApproval = activeProjectQuery.data?.status === 'pending_owner_approval' || activeProjectQuery.data?.status === 'pending_rab_approval';
  // Status 'draft' setelah proyek aktif dan ada riwayat penolakan = RAB/Baseline ditolak Owner, provider perlu revisi ulang.
  const hasRejectedApproval = (activeProjectQuery.data as any)?.approvals?.some((a: any) => a.status === 'rejected');
  const isRejectedProject = activeProjectQuery.data?.status === 'draft' && Boolean(activeProjectId) && Boolean(hasRejectedApproval);
  const isActiveApproval = false;

  const isBaselineLocked = activeProjectQuery.data?.status !== 'draft' || isPendingApproval;

  const isProjectLoading = Boolean(activeProjectId) && (
    activeProjectQuery.isLoading ||
    (activeProjectQuery.isFetching && String(activeProjectQuery.data?.id) !== String(activeProjectId))
  );

  const workspaceTitle = isProjectLoading
    ? "Memuat Proyek..."
    : (activeProjectQuery.data?.name || baselineData.projectName || "Kelola Proyek");

  const workspaceStatus = isProjectLoading
    ? "Memuat..."
    : workspaceSource === "draft"
      ? "Draft Rencana"
      : workspaceSource === "new"
        ? "Proyek Baru"
        : activeProjectId
          ? isPendingApproval
            ? "Menunggu Persetujuan RAB"
            : isRejectedProject
              ? "RAB Ditolak – Perlu Revisi"
              : activeProjectQuery.data?.status === 'draft'
                ? "Persiapan Baseline"
                : activeProjectQuery.data?.status === 'pending_bast_approval'
                  ? "Menunggu BAST"
                  : activeProjectQuery.data?.status === 'completed'
                    ? "Selesai"
                    : "Proyek Aktif"
          : "Belum Dipilih";

  const actualMilestones = activeProjectQuery.data?.milestones || baselineData.milestones || [];
  const actualTasks = activeProjectQuery.data?.milestones 
    ? activeProjectQuery.data.milestones.flatMap((m: any) => m.tasks || []) 
    : baselineData.tasks || [];

  const taskCount = isProjectLoading ? 0 : actualTasks.length;
  const milestoneCount = isProjectLoading ? 0 : actualMilestones.length;

  const progressAverage = isProjectLoading ? 0 : (
    taskCount > 0
      ? Math.round(
        actualTasks.reduce(
          (sum: number, task: any) => sum + (task.status === 'completed' ? 100 : Number(executionData.taskProgress[String(task.id)] || task.progress || 0)),
          0,
        ) / taskCount,
      )
      : 0
  );

  const isFinishedProject = activeProjectQuery.data?.status === 'completed' || activeProjectQuery.data?.status === 'pending_bast_approval' || activeProjectQuery.data?.status === 'pending_owner_approval';

  const nextAction = isProjectLoading
    ? "Mengambil data terbaru..."
    : isFinishedProject
      ? activeProjectQuery.data?.status === 'completed'
        ? "Proyek ini telah selesai dan ditutup secara internal."
        : "Menunggu persetujuan Berita Acara Serah Terima (BAST) dari Owner."
      : activeTab === "planning"
        ? "Lengkapi rencana kerja, milestone, budget, dan resource."
        : activeTab === "execution"
          ? "Monitor progress dan evidence dari mobile/operasional."
          : activeTab === "validation"
            ? "Review evidence, approve, atau kembalikan revisi."
            : "Siapkan laporan read-only untuk stakeholder.";




  const contextValue: ProjectManagementContextValue = {
    activeEvidence,
    activeProjectId,
    activeTaskId,
    activeValidationId,
    activeValidationItem,
    activeTab,
    allProjects,
    activeProjectData: activeProjectQuery.data ?? selectedProviderProject,
    allTechnicians,
    architectVendors,
    baselineData,
    currentVendorCosts,
    executionData,
    handleApproveValidation,
    handleBaselineChange,
    handleSaveBaseline,
    handleSaveBaselineDraft,
    handleFinalizeBaseline,
    handleAssignTech,
    handleDeleteProject,
    handleLoadProject,
    handleNewProject,
    handleRejectValidation,
    handleSaveManagerNote,
    handleTaskProgress,
    handleToggleMaterial,
    handleValidationCheck,
    isEvidenceModalOpen,
    isAllChecked,
    isCleaning,
    isConstruction,
    isHistoryModalOpen,
    isInventoryModalOpen,
    isLoadingProviders,
    isLoadingVendors,
    isRejectModalOpen,
    isTeamModalOpen,
    isVendorModalOpen,
    materialBudget,
    managerNote,
    newItem,
    newVendor,
    permissions,
    providers,
    rejectReason,
    selectedProvider,
    setActiveTab,
    setActiveEvidence,
    setActiveTaskId,
    setActiveValidationId,
    setBaselineData,
    handleAddExpenseApi,
    setIsEvidenceModalOpen,
    setIsHistoryModalOpen,
    setIsInventoryModalOpen,
    setIsRejectModalOpen,
    setIsTeamModalOpen,
    setIsVendorModalOpen,
    setNewItem,
    setNewVendor,
    setRejectReason,
    setSelectedProvider,
    setManagerNote,
    setTechSearch,
    taskTeams,
    techSearch,
    totalCalculatedExpense,
    userRole,
    isPendingApproval,
    isRejectedProject,
    isActiveApproval,
    isBaselineLocked,
    isProjectActive: activeProjectId
      ? ['active', 'in_progress', 'execution', 'converted_to_manpro', 'completed'].includes(String(activeProjectQuery.data?.status || '').toLowerCase())
      : true,
    validationChecklist,
    validationItems,
  };

  return (
    <ProjectManagementProvider value={contextValue}>
      <div className="max-w-[1400px] text-slate-800 mx-auto my-0  animate-[fadeIn_0.4s_ease-out_forwards]">
          {userRole === "provider" ? (
            <div className="mt-6">
              <ProviderProjectWorkspace
                providerManproProjects={providerManproProjects}
                providerManproProjectsQuery={providerManproProjectsQuery}
                selectedProviderProject={selectedProviderProject}
                activeProjectId={activeProjectId}
                activeProjectData={activeProjectQuery.data}
                isWorkspaceOpen={isWorkspaceOpen}
                isProjectLoading={isProjectLoading}
                baselineData={baselineData}
                executionData={executionData}
                workspaceTitle={workspaceTitle}
                workspaceStatus={workspaceStatus}
                nextAction={nextAction}
                progressAverage={progressAverage}
                milestoneCount={milestoneCount}
                taskCount={taskCount}
                isRejectedProject={isRejectedProject}
                isPendingApproval={isPendingApproval}
                activeTab={activeTab}
                onNewProject={handleNewProject}
                onOpenProject={openProviderProjectWorkspace}
                onSelectProject={setSelectedProviderProjectId}
                onCloseWorkspace={closeWorkspace}
                onShareLink={handleShareLink}
              />
            </div>
          )  : (
            <div className="mt-6">
              <div className="space-y-5">
              <ProjectWorkspaceOverview
                baselineData={baselineData}
                activeProjectId={activeProjectId}
                activeProjectData={activeProjectQuery.data}
                isProjectLoading={isProjectLoading}
                workspaceTitle={workspaceTitle}
                workspaceStatus={workspaceStatus}
                nextAction={nextAction}
                progressAverage={progressAverage}
                milestoneCount={milestoneCount}
                taskCount={taskCount}
                isRejectedProject={isRejectedProject}
                isPendingApproval={isPendingApproval}
                onClose={closeWorkspace}
                onShareLink={handleShareLink}
              />
                {isProjectLoading ? (
                  <ProjectDetailsSkeleton />
                ) : (
                  <>
                    <div className="mb-5">
                      <ProjectManagementTabs />
                    </div>
                    <div className="overflow-hidden rounded-3xl border border-slate-200 bg-white shadow-[0_10px_30px_rgba(0,0,0,0.05)]">
                      {activeTab === "planning" && <PlanningSection />}
                      {activeTab === "execution" && <ExecutionSection />}
                      {activeTab === "validation" && <ValidationSection />}
                      {activeTab === "reporting" && <ReportingSection />}
                      {activeTab === "team" && (
                        <TeamManagementPage
                          projectId={String(activeProjectId || "1")}
                          projectName={baselineData.projectName || "Proyek MANPRO"}
                        />
                      )}
                      {activeTab === "finance" && <ProjectManagementFinance />}
                    </div>
                  </>
                )}
              </div>
            </div>
          )}
          
        <ProjectManagementModals />

        <NewProjectModal
          isOpen={isNewProjectModalOpen}
          newProjectName={newProjectName}
          onClose={() => setIsNewProjectModalOpen(false)}
          onNameChange={setNewProjectName}
          onSubmit={handleCreateNewProject}
        />

        <FinalizeBaselineModal
          isOpen={isFinalizeModalOpen}
          onClose={() => setIsFinalizeModalOpen(false)}
          onConfirm={() => {
            setIsFinalizeModalOpen(false);
            handleSaveBaseline("final", true);
          }}
        />
      </div>
    </ProjectManagementProvider>
  );
};

export default ProjectManagement;